/**
 * Delivery Insurance Add-On v3
 */

class DeliveryInsuranceAddon {
  #modal = null;
  #modalAddBtn = null;
  #modalNote = null;
  #isLoading = false;

  constructor() {
    this.#modal = document.getElementById('delivery-insurance-modal');

    if (!this.#modal) {
      console.warn('[DI] Modal element not found — addon will not initialise');
      return;
    }

    this.#modalAddBtn = this.#modal.querySelector('[data-di-modal-add-btn]');
    this.#modalNote   = this.#modal.querySelector('[data-di-already-note]');

    this.#bindEvents();
    this.#syncInitialState();
    console.log('[DI] Initialised. Variant ID:', document.querySelector('[data-di-addon]')?.dataset?.variantId);
  }

  #bindEvents() {
    document.addEventListener('click', (e) => {
      const triggerBtn  = e.target.closest('[data-di-trigger-btn]');
      const infoBtn     = e.target.closest('[data-di-info-btn]');
      const closeBtn    = e.target.closest('[data-di-modal-close]');
      const modalAddBtn = e.target.closest('[data-di-modal-add-btn]');

      if (triggerBtn)   { e.preventDefault(); e.stopPropagation(); this.#handleTriggerClick(triggerBtn); return; }
      if (infoBtn)      { e.preventDefault(); e.stopPropagation(); this.#openModal(); return; }
      if (closeBtn)     { e.preventDefault(); e.stopPropagation(); this.#closeModal(); return; }
      if (modalAddBtn)  { e.preventDefault(); e.stopPropagation(); this.#handleAddClick(); return; }
    }, true); // ← capture phase so we intercept before any form handlers
  }

  #syncInitialState() {
    document.querySelectorAll('[data-di-addon]').forEach((addon) => {
      if (addon.dataset.inCart === 'true') this.#setAddedState(addon, true, false);
    });
  }

  #handleTriggerClick(btn) {
    const addon  = btn.closest('[data-di-addon]');
    const inCart = addon?.dataset.inCart === 'true';
    console.log('[DI] Trigger clicked. inCart:', inCart, 'variantId:', addon?.dataset?.variantId);
    if (inCart) { this.#showAlreadyAddedMessage(addon); return; }
    this.#addToCart(addon);
  }

  #handleAddClick() {
    const addon  = document.querySelector('[data-di-addon]');
    const inCart = addon?.dataset.inCart === 'true';
    if (inCart) { this.#closeModal(); return; }
    this.#addToCart(addon, () => this.#closeModal());
  }

  async #addToCart(addon, onSuccess) {
    if (this.#isLoading) return;

    const variantId = addon?.dataset?.variantId;
    if (!variantId) {
      console.error('[DI] No variantId found on addon element');
      return;
    }

    console.log('[DI] Adding to cart, variantId:', variantId);
    this.#isLoading = true;
    this.#setButtonsDisabled(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: { _addon_type: 'delivery_insurance', _order_addon: 'true' },
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('[DI] Added successfully:', data.title);

      // Update all addon instances immediately
      document.querySelectorAll('[data-di-addon]').forEach((el) => {
        el.dataset.inCart = 'true';
        this.#setAddedState(el, true, true);
      });

      this.#setButtonsDisabled(false);
      if (typeof onSuccess === 'function') onSuccess();

      // Refresh cart totals in background
      this.#refreshCartAsync();

    } catch (err) {
      console.error('[DI] Add to cart failed:', err.message);
      this.#setButtonsDisabled(false);
    } finally {
      this.#isLoading = false;
    }
  }

  #setButtonsDisabled(disabled) {
    document.querySelectorAll('[data-di-trigger-btn], [data-di-modal-add-btn]').forEach((btn) => {
      btn.disabled = disabled;
      btn.style.opacity = disabled ? '0.6' : '';
    });
  }

  #setAddedState(addon, added, animate) {
    if (!addon) return;
    const btn        = addon.querySelector('[data-di-trigger-btn]');
    const alreadyMsg = addon.querySelector('[data-di-already-msg]');
    if (!btn) return;

    if (added) {
      btn.classList.add('di-trigger__btn--added');
      btn.setAttribute('aria-label', 'Shipping Protection already added');
      btn.innerHTML = `
        <span class="di-trigger__btn-inner di-trigger__btn-inner--added">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Added
        </span>`;
      if (animate && alreadyMsg) {
        alreadyMsg.classList.remove('hidden');
        alreadyMsg.classList.add('di-already-added--animate');
        setTimeout(() => alreadyMsg.classList.remove('di-already-added--animate'), 2000);
      }
    } else {
      btn.classList.remove('di-trigger__btn--added');
      btn.setAttribute('aria-label', 'Add Shipping Protection to your order');
      btn.innerHTML = `
        <span class="di-trigger__btn-inner">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          Add
        </span>`;
      if (alreadyMsg) alreadyMsg.classList.add('hidden');
    }

    if (this.#modalAddBtn) {
      if (added) {
        this.#modalAddBtn.classList.add('di-modal__add-btn--added');
        this.#modalAddBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Shipping Protection Added`;
        if (this.#modalNote) this.#modalNote.removeAttribute('hidden');
      } else {
        this.#modalAddBtn.classList.remove('di-modal__add-btn--added');
        this.#modalAddBtn.textContent = 'Add Shipping Protection';
        if (this.#modalNote) this.#modalNote.setAttribute('hidden', '');
      }
    }
  }

  #showAlreadyAddedMessage(addon) {
    const msg = addon?.querySelector('[data-di-already-msg]');
    if (!msg) return;
    msg.classList.remove('hidden', 'di-already-added--flash');
    void msg.offsetWidth;
    msg.classList.add('di-already-added--flash');
    clearTimeout(this._flashTimeout);
    this._flashTimeout = setTimeout(() => msg.classList.remove('di-already-added--flash'), 2500);
  }

  #openModal() {
    if (!this.#modal || this.#modal.open) return;
    this.#modal.showModal();
  }

  #closeModal() {
    if (!this.#modal || !this.#modal.open) return;
    this.#modal.classList.add('di-modal--closing');

    const restore = () => {
      this.#modal.classList.remove('di-modal--closing');
      this.#modal.close();
    };

    this.#modal.addEventListener('animationend', restore, { once: true });
    setTimeout(restore, 350);
  }

  #refreshCartAsync() {
    if (window.location.pathname === '/cart') {
      window.location.reload();
      return;
    }

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      sectionIds.add(el.dataset.sectionId);
    });

    if (!sectionIds.size) return;

    fetch(`/cart?sections=${[...sectionIds].join(',')}`)
      .then((r) => r.json())
      .then((data) => {
        sectionIds.forEach((sectionId) => {
          const html    = data[sectionId];
          if (!html) return;
          const parser  = new DOMParser();
          const newDoc  = parser.parseFromString(html, 'text/html');
          const newEl   = newDoc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
          const current = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
          if (newEl && current) {
            current.innerHTML = newEl.innerHTML;
            current.querySelectorAll('[data-di-addon]').forEach((addon) => {
              addon.dataset.inCart = 'true';
              this.#setAddedState(addon, true, false);
            });
          }
        });
      })
      .catch(() => {});
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DeliveryInsuranceAddon());
} else {
  new DeliveryInsuranceAddon();
}
